import { useMemo } from 'react';
import type { NetWorthSnapshot } from '../types';
import { formatEuro } from '../utils/formatters';
import styles from './NetWorthTrendChart.module.css';

type Props = {
  snapshots: NetWorthSnapshot[];
  loading?: boolean;
  error?: string | null;
};

type TrendPoint = {
  date: string;
  netWorth: number;
  label: string;
};

const CHART_WIDTH = 640;
const CHART_HEIGHT = 200;
const MARGIN = { top: 16, right: 16, bottom: 32, left: 58 };
const GRID_LINE_COUNT = 4;

function formatShortDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

function formatCompactEuro(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} M €`;
  }
  if (abs >= 10_000) {
    return `${(value / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} k €`;
  }
  return formatEuro(value);
}

function formatSignedCompactEuro(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
  return `${sign}${formatCompactEuro(Math.abs(value))}`;
}

function buildTrendPoints(snapshots: NetWorthSnapshot[]): TrendPoint[] {
  return snapshots.map((snapshot) => ({
    date: snapshot.snapshotDate,
    netWorth: snapshot.combined.netWorth,
    label: formatShortDate(snapshot.snapshotDate),
  }));
}

function buildChartGeometry(points: TrendPoint[]) {
  const innerWidth = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
  const values = points.map((point) => point.netWorth);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const span = maxValue - minValue;
  const padding = span > 0 ? span * 0.12 : Math.max(Math.abs(maxValue) * 0.05, 1000);
  const yMin = minValue - padding;
  const yMax = maxValue + padding;
  const yRange = yMax - yMin || 1;

  const xScale = (index: number) =>
    MARGIN.left + (index / Math.max(points.length - 1, 1)) * innerWidth;
  const yScale = (value: number) =>
    MARGIN.top + ((yMax - value) / yRange) * innerHeight;

  const coordinates = points.map((point, index) => ({
    ...point,
    x: xScale(index),
    y: yScale(point.netWorth),
    delta: index === 0 ? null : point.netWorth - points[index - 1].netWorth,
  }));

  const linePath = coordinates
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(' ');

  const areaPath = `${linePath} L ${coordinates[coordinates.length - 1].x.toFixed(1)} ${(MARGIN.top + innerHeight).toFixed(1)} L ${coordinates[0].x.toFixed(1)} ${(MARGIN.top + innerHeight).toFixed(1)} Z`;

  const baselineY = MARGIN.top + innerHeight;
  const horizontalGridLines = Array.from({ length: GRID_LINE_COUNT }, (_, index) => {
    const ratio = index / (GRID_LINE_COUNT - 1);
    const value = yMax - ratio * yRange;
    return {
      y: yScale(value),
      value,
      label: formatCompactEuro(value),
      isBaseline: index === GRID_LINE_COUNT - 1,
    };
  });

  const verticalGridCount = Math.min(Math.max(points.length - 1, 3), 5);
  const verticalGridLines = Array.from({ length: verticalGridCount + 1 }, (_, index) =>
    MARGIN.left + (index / verticalGridCount) * innerWidth,
  );

  return {
    coordinates,
    linePath,
    areaPath,
    innerHeight,
    innerWidth,
    yMin,
    yMax,
    horizontalGridLines,
    verticalGridLines,
    baselineY,
  };
}

export default function NetWorthTrendChart({ snapshots, loading = false, error = null }: Props) {
  const points = useMemo(() => buildTrendPoints(snapshots), [snapshots]);
  const geometry = useMemo(
    () => (points.length >= 2 ? buildChartGeometry(points) : null),
    [points],
  );

  const chartSummary = useMemo(() => {
    if (points.length < 2) return '';
    const first = points[0];
    const last = points[points.length - 1];
    const delta = last.netWorth - first.netWorth;
    const deltaLabel = delta >= 0 ? `up ${formatEuro(delta)}` : `down ${formatEuro(Math.abs(delta))}`;
    return `Combined net worth from ${formatEuro(first.netWorth)} on ${first.label} to ${formatEuro(last.netWorth)} on ${last.label}, ${deltaLabel}.`;
  }, [points]);

  if (loading) {
    return (
      <section className={styles.section} aria-label="Net worth trend" aria-busy="true">
        <p className={styles.hint}>Loading history…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.section} aria-label="Net worth trend">
        <p className={styles.hint}>{error}</p>
      </section>
    );
  }

  if (points.length < 2) {
    return (
      <section className={styles.section} aria-label="Net worth trend">
        <p className={styles.hint}>Check back tomorrow.</p>
      </section>
    );
  }

  const first = points[0];
  const last = points[points.length - 1];

  return (
    <section className={styles.section} aria-label="Net worth trend">
      <figure className={styles.chart}>
        <div className={styles.chartPanel}>
          <svg
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            className={styles.svg}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={chartSummary}
          >
            <defs>
              <linearGradient id="netWorthAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.28" />
                <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
              </linearGradient>
              <clipPath id="netWorthAreaClip">
                <rect
                  className={styles.areaClipRect}
                  x={MARGIN.left}
                  y={MARGIN.top}
                  width={geometry!.innerWidth}
                  height={geometry!.innerHeight}
                />
              </clipPath>
            </defs>

            <g className={styles.gridGroup} aria-hidden="true">
              {geometry!.verticalGridLines.map((x) => (
                <line
                  key={`v-${x.toFixed(1)}`}
                  x1={x}
                  x2={x}
                  y1={MARGIN.top}
                  y2={geometry!.baselineY}
                  className={styles.gridLineVertical}
                />
              ))}

              {geometry!.horizontalGridLines.map((gridLine) => (
                <g key={gridLine.label}>
                  <line
                    x1={MARGIN.left}
                    x2={CHART_WIDTH - MARGIN.right}
                    y1={gridLine.y}
                    y2={gridLine.y}
                    className={gridLine.isBaseline ? styles.gridLineBaseline : styles.gridLine}
                  />
                  <text
                    x={MARGIN.left - 8}
                    y={gridLine.y + 4}
                    className={styles.yAxisLabel}
                    textAnchor="end"
                  >
                    {gridLine.label}
                  </text>
                </g>
              ))}
            </g>

            <path
              d={geometry!.areaPath}
              className={styles.area}
              fill="url(#netWorthAreaGradient)"
              clipPath="url(#netWorthAreaClip)"
              aria-hidden="true"
            />
            <path
              d={geometry!.linePath}
              className={styles.line}
              pathLength={1}
              aria-hidden="true"
            />

            {geometry!.coordinates.map((point, index) => (
              <circle
                key={point.date}
                cx={point.x}
                cy={point.y}
                r={4}
                className={styles.dot}
                style={{ animationDelay: `${0.75 + index * 0.12}s` }}
                aria-hidden="true"
              >
                <title>{`${point.label}: ${formatEuro(point.netWorth)}`}</title>
              </circle>
            ))}

            {geometry!.coordinates.map((point, index) => {
              if (point.delta === null) return null;
              const isLast = index === geometry!.coordinates.length - 1;
              const deltaClass =
                point.delta > 0
                  ? styles.deltaLabelPositive
                  : point.delta < 0
                    ? styles.deltaLabelNegative
                    : styles.deltaLabel;
              return (
                <text
                  key={`delta-${point.date}`}
                  x={point.x}
                  y={point.y - 10}
                  className={deltaClass}
                  textAnchor={isLast ? 'end' : 'middle'}
                  style={{ animationDelay: `${0.9 + index * 0.12}s` }}
                  aria-hidden="true"
                >
                  {formatSignedCompactEuro(point.delta)}
                </text>
              );
            })}

            <text
              x={MARGIN.left}
              y={CHART_HEIGHT - 10}
              className={styles.axisLabel}
              textAnchor="start"
            >
              {first.label}
            </text>
            <text
              x={CHART_WIDTH - MARGIN.right}
              y={CHART_HEIGHT - 10}
              className={styles.axisLabel}
              textAnchor="end"
            >
              {last.label}
            </text>
          </svg>
        </div>
      </figure>
    </section>
  );
}
